import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';

/* SSi website – shared components for the free Welsh resources.
 *
 * Three components cover everything we need for the four pages:
 *
 *   RHero          hero with kicker/title/meta chips (level, duration)
 *   LyricSong      side-by-side Welsh / English lyrics with a play bar
 *   ResourceGrid   card grid used by the /welsh/resources index
 *   EpisodeList    episode-list component used by bedtime-welsh
 *   RTakeItFurther bottom CTA card
 *
 * All copy is driven via props so every page stays tweakable.
 */

export function RHero({ t, chips }) {
  return (
    <section className="ssi-res__hero" style={rHeroStyles.wrap}>
      <div style={rHeroStyles.inner}>
        <nav style={rHeroStyles.crumbs} aria-label="Breadcrumbs">
          <Link to="/" style={rHeroStyles.crumbLink}>Home</Link>
          <span style={rHeroStyles.crumbSep}>·</span>
          <Link to="/welsh/resources" style={rHeroStyles.crumbLink}>Welsh resources</Link>
          {t.rCrumb && (
            <>
              <span style={rHeroStyles.crumbSep}>·</span>
              <span style={rHeroStyles.crumbCurr}>{t.rCrumb}</span>
            </>
          )}
        </nav>

        <div style={rHeroStyles.kicker}>{t.rKicker}</div>
        <h1 style={rHeroStyles.h1}>{t.rTitle}</h1>
        {t.rLede && <p style={rHeroStyles.lede}>{t.rLede}</p>}

        {chips && chips.length > 0 && (
          <div style={rHeroStyles.chipRow}>
            {chips.map((c, i) => (
              <div key={i} style={rHeroStyles.chip}>
                <span style={rHeroStyles.chipKey}>{c.key}</span>
                <span style={rHeroStyles.chipVal}>{c.val}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ---------- Lyric song ---------------------------------------- */
/* Side-by-side Welsh (large) / English (muted) with a minimal play
 * bar along the top. Accepts `verses` as an array of
 *   { cy: ['line', 'line'], en: ['line', 'line'] }
 * so verse structure is preserved. */

export function LyricSong({ t, verses, audioSrc, hideAudio }) {
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur]   = useState(0);
  const audioRef = useRef(null);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); } else { a.play().catch(()=>{}); }
  };

  const onTime = () => setTime(audioRef.current.currentTime);
  const onMeta = () => setDur(audioRef.current.duration || 0);
  const onEnd  = () => { setPlaying(false); setTime(0); };

  const fmt = (s) => {
    if (!isFinite(s)) return '0:00';
    const m = Math.floor(s / 60); const ss = Math.floor(s % 60);
    return `${m}:${ss.toString().padStart(2,'0')}`;
  };
  const pct = dur ? (time / dur) * 100 : 0;

  return (
    <section className="ssi-res__song" style={songStyles.wrap}>
      <div style={songStyles.head}>
        <div style={songStyles.kicker}>{t.songKicker}</div>
        <h2 style={songStyles.h2}>{t.songTitle}</h2>
        {t.songSub && <p style={songStyles.sub}>{t.songSub}</p>}
      </div>

      {/* Play bar */}
      {!hideAudio && <div style={songStyles.playBar}>
        <button type="button" onClick={toggle} style={songStyles.playBtn} aria-label={playing ? 'Pause' : 'Play'}>
          {playing
            ? <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><rect x="3" y="2" width="4" height="14" fill="#fff"/><rect x="11" y="2" width="4" height="14" fill="#fff"/></svg>
            : <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M4 2v14l12-7L4 2z" fill="#fff"/></svg>
          }
        </button>
        <div style={songStyles.barOuter}>
          <div style={{...songStyles.barFill, width:`${pct}%`}}/>
        </div>
        <div style={songStyles.time}>{fmt(time)} / {fmt(dur)}</div>
        <audio
          ref={audioRef}
          src={audioSrc}
          onPlay={()=>setPlaying(true)}
          onPause={()=>setPlaying(false)}
          onTimeUpdate={onTime}
          onLoadedMetadata={onMeta}
          onEnded={onEnd}
          preload="metadata"
        />
      </div>}

      {/* Verses */}
      <div style={songStyles.versesWrap}>
        {verses.map((v, i) => (
          <div key={i} style={songStyles.verse}>
            <div style={songStyles.verseLabel}>{t.songVerseLabel || 'Verse'} {i + 1}</div>
            <div className="ssi-res__verseGrid" style={songStyles.verseGrid}>
              <div style={songStyles.cy}>
                {v.cy.map((line, j) => <div key={j} style={songStyles.lineCy}>{line}</div>)}
              </div>
              <div style={songStyles.en}>
                {v.en.map((line, j) => <div key={j} style={songStyles.lineEn}>{line}</div>)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pronunciation tips */}
      {t.songTipsTitle && (
        <div style={songStyles.tips}>
          <div style={songStyles.tipsLabel}>{t.songTipsTitle}</div>
          <p style={songStyles.tipsBody}>{t.songTipsBody}</p>
        </div>
      )}
    </section>
  );
}

/* ---------- Episode list (for Bedtime Welsh) ------------------- */

export function EpisodeList({ t, episodes }) {
  const [open, setOpen] = useState(null);
  const audioRef = useRef(null);

  const toggle = (slug) => {
    if (open === slug) {
      setOpen(null);
      if (audioRef.current) audioRef.current.pause();
    } else {
      setOpen(slug);
      // Let react render, then play
      setTimeout(() => audioRef.current && audioRef.current.play().catch(()=>{}), 40);
    }
  };

  return (
    <section className="ssi-res__eps" style={epStyles.wrap}>
      <div style={epStyles.head}>
        <div style={epStyles.kicker}>{t.episodesKicker}</div>
        <h2 style={epStyles.h2}>{t.episodesTitle}</h2>
        {t.episodesLede && <p style={epStyles.lede}>{t.episodesLede}</p>}
      </div>

      <ol style={epStyles.list}>
        {episodes.map((e, i) => {
          const isOpen = open === e.slug;
          return (
            <li key={e.slug} style={{...epStyles.row, borderColor: isOpen ? 'var(--brandRed)' : 'var(--border)'}}>
              <button type="button" onClick={() => toggle(e.slug)} style={epStyles.rowHead}>
                <span style={epStyles.epNum}>{String(i + 1).padStart(2, '0')}</span>
                <span style={epStyles.epBody}>
                  <span style={epStyles.epTitle}>{e.title}</span>
                  <span style={epStyles.epMeta}>
                    <span style={epStyles.epChip}>{e.level}</span>
                    <span>·</span>
                    <span>{e.dur}</span>
                    <span>·</span>
                    <span style={{color:'var(--grey)'}}>{e.date}</span>
                  </span>
                </span>
                <span style={{...epStyles.epPlay, background: isOpen ? 'var(--brandRed)' : '#fff', color: isOpen ? '#fff' : 'var(--brandRed)', borderColor: 'var(--brandRed)'}}>
                  {isOpen
                    ? <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><rect x="2" y="1" width="3" height="10" fill="currentColor"/><rect x="7" y="1" width="3" height="10" fill="currentColor"/></svg>
                    : <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 1v10l9-5L2 1z" fill="currentColor"/></svg>
                  }
                </span>
              </button>
              {isOpen && (
                <div style={epStyles.rowOpen}>
                  <p style={epStyles.epBlurb}>{e.blurb}</p>
                  <audio ref={audioRef} src={e.src} controls style={{width:'100%', marginTop:12}} preload="metadata"/>
                  {e.transcript && (
                    <details style={epStyles.trans}>
                      <summary style={epStyles.transSum}>{t.episodesTranscript || 'Transcript'}</summary>
                      <div style={epStyles.transBody}>
                        {e.transcript.map((p, j) => <p key={j} style={epStyles.transP}>{p}</p>)}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/* ---------- Resource index grid (cards) ----------------------- */
/* Card hrefs may be SPA paths (e.g. '/welsh/calon-lan'), hash anchors,
 * or external URLs. Pick <Link> for SPA paths, plain <a> otherwise. */

function GridCard({ c }) {
  const href = c.href || '#';
  const isSpa = href && !/^(https?:|mailto:|tel:|#)/.test(href) && href !== '#';
  const inner = (
    <>
      <div style={gridStyles.cardBadge}>{c.badge}</div>
      <div style={gridStyles.cardIcon} aria-hidden="true">{c.icon}</div>
      <h3 style={gridStyles.cardTitle}>{c.title}</h3>
      <p style={gridStyles.cardBlurb}>{c.blurb}</p>
      <div style={gridStyles.cardMeta}>
        <span>{c.meta}</span>
        <span style={gridStyles.cardArrow}>→</span>
      </div>
    </>
  );
  const style = {...gridStyles.card, background: c.bg || '#fff'};
  if (isSpa) {
    return <Link to={href} style={style}>{inner}</Link>;
  }
  return <a href={href} style={style}>{inner}</a>;
}

export function ResourceGrid({ sections }) {
  return (
    <div className="ssi-res__grid" style={gridStyles.wrap}>
      {sections.map((sec, si) => (
        <section key={si} className="ssi-res__section" style={gridStyles.section}>
          <header style={gridStyles.secHead}>
            <div style={gridStyles.secKicker}>{sec.kicker}</div>
            <h2 style={gridStyles.secTitle}>{sec.title}</h2>
            {sec.sub && <p style={gridStyles.secSub}>{sec.sub}</p>}
          </header>
          <div className="ssi-res__cards" style={{...gridStyles.cards, gridTemplateColumns: sec.cols === 2 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'}}>
            {sec.cards.map((c, ci) => <GridCard key={ci} c={c}/>)}
          </div>
        </section>
      ))}
    </div>
  );
}

/* ---------- Mini quote band (3 testimonials) -------------------- */
/* A slim 3-column band of short testimonials, for use on resource
 * pages. Accepts up to 3 quotes via tweak keys q1/n1/r1 … q3/n3/r3
 * plus a heading (bandKicker/bandTitle). Falls back to initials when
 * a quote has no photo. */

export function QuoteBand({ t }) {
  const quotes = [
    { q: t.bandQ1, n: t.bandN1, r: t.bandR1, photo: t.bandPhoto1 },
    { q: t.bandQ2, n: t.bandN2, r: t.bandR2, photo: t.bandPhoto2 },
    { q: t.bandQ3, n: t.bandN3, r: t.bandR3, photo: t.bandPhoto3 },
  ].filter(o => o.q && o.n);
  if (quotes.length === 0) return null;
  const initials = (n) => n.split(/\s+/).filter(Boolean).slice(0,2).map(w=>w[0].toUpperCase()).join('');
  return (
    <section className="ssi-res__band" style={bandStyles.wrap}>
      <div style={bandStyles.head}>
        <div style={bandStyles.kicker}>{t.bandKicker}</div>
        <h2 style={bandStyles.h2}>{t.bandTitle}</h2>
      </div>
      <div className="ssi-res__bandGrid" style={bandStyles.grid}>
        {quotes.map((o, i) => (
          <figure key={i} style={bandStyles.card}>
            <blockquote style={bandStyles.q}>
              <span style={bandStyles.qMark}>“</span>{o.q}
            </blockquote>
            <figcaption style={bandStyles.cap}>
              {o.photo
                ? <img src={`/assets/${o.photo}`} alt="" style={bandStyles.photo}/>
                : <div style={bandStyles.mono}>{initials(o.n)}</div>}
              <div>
                <div style={bandStyles.name}>{o.n}</div>
                <div style={bandStyles.role}>{o.r}</div>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

/* ---------- Take it further CTA ------------------------------- */

export function RTakeItFurther({ t }) {
  return (
    <section className="ssi-res__tif" style={tifStyles.wrap}>
      <div className="ssi-res__tifCard" style={tifStyles.card}>
        <div style={tifStyles.copy}>
          <div style={tifStyles.kicker}>{t.rTakeKicker}</div>
          <h2 style={tifStyles.h2}>{t.rTakeTitle}</h2>
          <p style={tifStyles.body}>{t.rTakeBody}</p>
          <div style={tifStyles.ctaRow}>
            <Link to="/pricing" className="btn btn--primary">{t.rTakeCta1}</Link>
            <Link to="/#sample" className="btn btn--ghost">{t.rTakeCta2}</Link>
          </div>
        </div>
        <div style={tifStyles.side}>
          <div style={tifStyles.sideQuote}>
            <span style={tifStyles.sideQuoteMark}>“</span>
            {t.rTakeQuote}
          </div>
          <div style={tifStyles.sideAttr}>
            <strong>{t.rTakeQuoteName}</strong> · {t.rTakeQuoteRole}
          </div>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
 * Styles
 * ========================================================= */

const rHeroStyles = {
  wrap: { background:'var(--offWhite, #FDF5F5)', borderBottom:'1px solid var(--border)', padding:'40px 32px 60px' },
  inner: { maxWidth:1080, margin:'0 auto' },
  crumbs: { display:'flex', gap:8, fontSize:12.5, color:'var(--grey)', marginBottom:28, alignItems:'center' },
  crumbLink: { color:'var(--grey)', textDecoration:'none' },
  crumbSep: { opacity:.5 },
  crumbCurr: { color:'var(--fg-1)', fontWeight:500 },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:16 },
  h1: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:60, lineHeight:1.05, letterSpacing:'-.02em', margin:'0 0 20px', textWrap:'balance', maxWidth:'18ch' },
  lede: { fontSize:19, lineHeight:1.55, color:'var(--grey)', margin:'0 0 32px', maxWidth:'56ch', textWrap:'pretty' },
  chipRow: { display:'flex', gap:12, flexWrap:'wrap' },
  chip: { display:'inline-flex', alignItems:'baseline', gap:8, padding:'8px 14px', background:'#fff', border:'1px solid var(--border)', borderRadius:999, fontSize:13 },
  chipKey: { fontSize:10.5, letterSpacing:'.12em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700 },
  chipVal: { color:'var(--fg-1)', fontWeight:500 },
};

const songStyles = {
  wrap: { padding:'64px 32px 32px', maxWidth:960, margin:'0 auto' },
  head: { marginBottom:28 },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:10 },
  h2: { fontFamily:'var(--font-display)', fontSize:34, lineHeight:1.15, margin:'0 0 10px', letterSpacing:'-.01em' },
  sub: { fontSize:15, color:'var(--grey)', margin:0, maxWidth:'62ch', lineHeight:1.55, textWrap:'pretty' },

  playBar: { display:'flex', alignItems:'center', gap:16, padding:'14px 18px', background:'var(--brandRed)', color:'#fff', borderRadius:14, marginBottom:36, boxShadow:'0 6px 16px -8px rgba(73,3,0,.5)' },
  playBtn: { width:44, height:44, borderRadius:'50%', border:'1.5px solid rgba(255,255,255,.65)', background:'rgba(0,0,0,.15)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', padding:0, paddingLeft:2, flexShrink:0 },
  barOuter: { flex:1, height:4, background:'rgba(255,255,255,.28)', borderRadius:2, overflow:'hidden' },
  barFill: { height:'100%', background:'var(--gold)', transition:'width 120ms linear' },
  time: { fontSize:12.5, color:'rgba(255,255,255,.85)', fontVariantNumeric:'tabular-nums', minWidth:90, textAlign:'right' },

  versesWrap: { display:'flex', flexDirection:'column', gap:36 },
  verse: {},
  verseLabel: { fontSize:11, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--grey)', fontWeight:700, marginBottom:12 },
  verseGrid: { display:'grid', gridTemplateColumns:'1.15fr 1fr', gap:36, padding:'24px 28px', background:'#fff', border:'1px solid var(--border)', borderRadius:16 },
  cy: {},
  en: { borderLeft:'1px solid var(--border)', paddingLeft:30 },
  lineCy: { fontFamily:'var(--font-display)', fontSize:20, lineHeight:1.55, color:'var(--fg-1)', letterSpacing:'-.005em' },
  lineEn: { fontFamily:'var(--font-body)', fontSize:15.5, lineHeight:1.65, color:'var(--grey)', fontStyle:'italic' },

  tips: { marginTop:40, padding:'24px 28px', background:'var(--offWhite, #FDF5F5)', border:'1px solid var(--border)', borderRadius:16 },
  tipsLabel: { fontSize:11, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:10 },
  tipsBody: { fontSize:15, lineHeight:1.6, color:'var(--fg-1)', margin:0, textWrap:'pretty' },
};

const epStyles = {
  wrap: { padding:'64px 32px 32px', maxWidth:900, margin:'0 auto' },
  head: { marginBottom:32 },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:10 },
  h2: { fontFamily:'var(--font-display)', fontSize:34, lineHeight:1.15, margin:'0 0 10px', letterSpacing:'-.01em' },
  lede: { fontSize:15.5, color:'var(--grey)', margin:0, maxWidth:'60ch', lineHeight:1.6, textWrap:'pretty' },
  list: { listStyle:'none', padding:0, margin:0, display:'flex', flexDirection:'column', gap:10 },
  row: { border:'1px solid', borderRadius:14, background:'#fff', overflow:'hidden', transition:'border-color 120ms' },
  rowHead: { display:'flex', alignItems:'center', gap:16, width:'100%', padding:'18px 20px', background:'none', border:'none', textAlign:'left', cursor:'pointer', font:'inherit' },
  epNum: { fontFamily:'var(--font-display)', fontSize:22, color:'var(--brandRed)', letterSpacing:'.02em', fontVariantNumeric:'tabular-nums', width:36, flexShrink:0 },
  epBody: { flex:1, minWidth:0, display:'flex', flexDirection:'column', gap:4 },
  epTitle: { fontFamily:'var(--font-display)', fontSize:17, color:'var(--fg-1)', lineHeight:1.3 },
  epMeta: { fontSize:12, color:'var(--grey)', display:'flex', gap:8, alignItems:'center' },
  epChip: { fontSize:10.5, letterSpacing:'.1em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, border:'1px solid var(--brandRed)', padding:'1px 7px', borderRadius:999 },
  epPlay: { width:34, height:34, borderRadius:'50%', border:'1.5px solid', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all 120ms', paddingLeft:1 },
  rowOpen: { padding:'0 20px 20px 72px', borderTop:'1px solid var(--border)' },
  epBlurb: { fontSize:14.5, color:'var(--grey)', lineHeight:1.6, margin:'14px 0 0', textWrap:'pretty' },
  trans: { marginTop:14 },
  transSum: { fontSize:12.5, color:'var(--brandRed)', cursor:'pointer', fontWeight:600, letterSpacing:'.04em', listStyle:'none' },
  transBody: { marginTop:10, paddingTop:10, borderTop:'1px dashed var(--border)' },
  transP: { fontSize:14, color:'var(--fg-1)', lineHeight:1.6, margin:'0 0 10px' },
};

const gridStyles = {
  wrap: { padding:'56px 32px 80px', maxWidth:1240, margin:'0 auto', display:'flex', flexDirection:'column', gap:64 },
  section: {},
  secHead: { marginBottom:28, maxWidth:720 },
  secKicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:10 },
  secTitle: { fontFamily:'var(--font-display)', fontSize:30, lineHeight:1.15, margin:'0 0 8px', letterSpacing:'-.01em' },
  secSub: { fontSize:15, color:'var(--grey)', margin:0, lineHeight:1.6, textWrap:'pretty' },
  cards: { display:'grid', gap:18 },
  card: { display:'block', padding:'28px 28px 26px', borderRadius:18, border:'1px solid var(--border)', textDecoration:'none', color:'inherit', position:'relative', transition:'transform 140ms, box-shadow 140ms, border-color 140ms', boxShadow:'0 1px 2px rgba(73,3,0,.04)' },
  cardBadge: { display:'inline-block', fontSize:10.5, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:12 },
  cardIcon: { fontSize:28, lineHeight:1, marginBottom:14, color:'var(--brandRed)' },
  cardTitle: { fontFamily:'var(--font-display)', fontSize:22, margin:'0 0 10px', lineHeight:1.2, letterSpacing:'-.005em' },
  cardBlurb: { fontSize:14.5, color:'var(--grey)', lineHeight:1.6, margin:'0 0 20px', textWrap:'pretty', minHeight:66 },
  cardMeta: { display:'flex', justifyContent:'space-between', alignItems:'baseline', fontSize:12.5, color:'var(--brandRed)', fontWeight:500, paddingTop:16, borderTop:'1px solid var(--border)' },
  cardArrow: { fontSize:16 },
};

const bandStyles = {
  wrap: { padding:'56px 32px 16px', maxWidth:1240, margin:'0 auto' },
  head: { textAlign:'center', maxWidth:640, margin:'0 auto 28px' },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:10 },
  h2: { fontFamily:'var(--font-display)', fontSize:28, lineHeight:1.2, margin:0, letterSpacing:'-.01em' },
  grid: { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:18 },
  card: { background:'#fff', border:'1px solid var(--border)', borderRadius:16, padding:'26px 24px 22px', margin:0, display:'flex', flexDirection:'column', gap:18, minHeight:220 },
  q: { fontFamily:'var(--font-display)', fontSize:17, lineHeight:1.45, color:'var(--fg-1)', margin:0, flex:1, textWrap:'pretty', letterSpacing:'-.005em' },
  qMark: { color:'var(--brandRed)', marginRight:4 },
  cap: { display:'flex', alignItems:'center', gap:12, paddingTop:16, borderTop:'1px solid var(--border)', margin:0 },
  photo: { width:40, height:40, borderRadius:'50%', objectFit:'cover', objectPosition:'center 28%' },
  mono: { width:40, height:40, borderRadius:'50%', background:'var(--offWhite, #FDF5F5)', border:'1px solid var(--border)', color:'var(--brandRed)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-display)', fontSize:14, flexShrink:0 },
  name: { fontSize:14, fontWeight:600, color:'var(--fg-1)' },
  role: { fontSize:12, color:'var(--grey)', marginTop:1 },
};

const tifStyles = {
  wrap: { padding:'56px 32px 96px', maxWidth:1240, margin:'0 auto' },
  card: { background:'var(--offWhite, #FDF5F5)', border:'1px solid var(--border)', borderRadius:22, display:'grid', gridTemplateColumns:'1.2fr 1fr', overflow:'hidden' },
  copy: { padding:'48px 52px' },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:12 },
  h2: { fontFamily:'var(--font-display)', fontSize:36, lineHeight:1.1, margin:'0 0 14px', letterSpacing:'-.01em', textWrap:'balance' },
  body: { fontSize:16, color:'var(--grey)', lineHeight:1.6, margin:'0 0 24px', textWrap:'pretty' },
  ctaRow: { display:'flex', gap:12, flexWrap:'wrap' },
  side: { padding:'48px 42px', background:'var(--brandRed)', color:'#fff', display:'flex', flexDirection:'column', justifyContent:'center' },
  sideQuote: { fontFamily:'var(--font-display)', fontSize:22, lineHeight:1.35, margin:'0 0 18px', color:'#fff', textWrap:'pretty' },
  sideQuoteMark: { color:'var(--gold)', marginRight:4 },
  sideAttr: { fontSize:13.5, color:'rgba(255,255,255,.82)', letterSpacing:'.01em' },
};
